import React, { useState, useEffect } from "react";
import {
  Heart,
  GraduationCap,
  Cross,
  HandHeart,
  Home,
  Image,
  Eye,
  Users,
  Handshake,
} from "lucide-react";

const contributions = [
  {
    title: "Founder, Go Dharmic Nepal",
    detail:
      "Comprehensive work on education, health, and community relief projects across rural Nepal.",
    icon: Handshake,
    color: "from-blue-600 to-cyan-600",
    type: "Organization",
    impact: "500+ lives touched",
    images: [
      "https://images.unsplash.com/photo-1593113598332-cd288d649433?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=600&h=400&fit=crop",
    ],
    description:
      "Established Go Dharmic Nepal to create sustainable impact in education, healthcare, and community development. Our holistic approach addresses multiple challenges faced by rural communities.",
    achievements: [
      "Community Centers Built",
      "Educational Programs",
      "Healthcare Initiatives",
      "Disaster Relief",
    ],
  },
  {
    title: "Educational Scholarships",
    detail:
      "Over 50 students supported with scholarships in Sindhupalchok, Nuwakot, and Jumla districts.",
    icon: GraduationCap,
    color: "from-green-600 to-emerald-600",
    type: "Education",
    impact: "50+ students supported",
    images: [
      "https://images.unsplash.com/photo-1427348693976-99e4aca06bf9?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1497486751825-1233686d5d80?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1581726690015-c9861fa5057f?w=600&h=400&fit=crop",
    ],
    description:
      "Providing educational opportunities to underprivileged students across multiple districts, focusing on sustainable support that enables long-term success and community development.",
    achievements: [
      "Full Scholarships",
      "Educational Materials",
      "Mentorship Programs",
      "Career Guidance",
    ],
  },
  {
    title: "Health Camps",
    detail:
      "Organized 4 major health camps in collaboration with Top of the World Adventure, providing free medical services.",
    icon: Cross,
    color: "from-red-600 to-pink-600",
    type: "Healthcare",
    impact: "1000+ patients treated",
    images: [
      "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1585435557343-3b092031e831?w=600&h=400&fit=crop",
    ],
    description:
      "Mobile health camps bringing essential medical services to remote areas with limited healthcare access. Our medical teams provide free consultations, treatments, and health education.",
    achievements: [
      "Free Medical Checkups",
      "Medicine Distribution",
      "Health Education",
      "Specialist Consultations",
    ],
  },
  {
    title: "COVID-19 Relief (2020)",
    detail:
      "Led comprehensive food distribution campaigns during pandemic lockdowns, supporting vulnerable families.",
    icon: HandHeart,
    color: "from-orange-600 to-yellow-600",
    type: "Emergency Relief",
    impact: "300+ families aided",
    images: [
      "https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1586095985310-10051bdccba3?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1586095985339-8b3e1e6bfbe2?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?w=600&h=400&fit=crop",
    ],
    description:
      "During the challenging COVID-19 pandemic, we mobilized resources to provide essential food supplies and support to families affected by lockdowns and economic hardship.",
    achievements: [
      "Food Package Distribution",
      "Emergency Supplies",
      "Community Support",
      "Safety Protocol Implementation",
    ],
  },
  {
    title: "Sanskrit Hostel & Library Maintenance",
    detail:
      "Ongoing support for educational infrastructure including hostel facilities and library resources.",
    icon: Home,
    color: "from-purple-600 to-violet-600",
    type: "Infrastructure",
    impact: "Ongoing impact",
    images: [
      "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=600&h=400&fit=crop",
    ],
    description:
      "Maintaining and improving educational infrastructure to ensure students have access to quality accommodation and learning resources for their academic pursuits.",
    achievements: [
      "Facility Maintenance",
      "Library Resources",
      "Student Accommodation",
      "Learning Environment",
    ],
  },
];

const impactStats = [
  {
    icon: Users,
    number: "1000+",
    label: "Lives Impacted",
    description: "Direct beneficiaries",
  },
  {
    icon: GraduationCap,
    number: "50+",
    label: "Students",
    description: "Scholarships provided",
  },
  {
    icon: Cross,
    number: "4",
    label: "Health Camps",
    description: "Medical outreach programs",
  },
  {
    icon: HandHeart,
    number: "300+",
    label: "Families",
    description: "COVID relief support",
  },
];

export default function Contribution() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [visibleItems, setVisibleItems] = useState([]);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => [...prev, entry.target.dataset.index]);
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".contribution-item").forEach((item, index) => {
      item.dataset.index = index;
      observer.observe(item);
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, []);

  const openGallery = (projectIndex, imageIndex = 0) => {
    setSelectedProject(projectIndex);
    setSelectedImage(imageIndex);
  };

  const closeGallery = () => {
    setSelectedProject(null);
    setSelectedImage(0);
  };

  const nextImage = () => {
    if (selectedProject !== null) {
      setSelectedImage(
        (prev) => (prev + 1) % contributions[selectedProject].images.length
      );
    }
  };

  const prevImage = () => {
    if (selectedProject !== null) {
      setSelectedImage((prev) =>
        prev === 0 ? contributions[selectedProject].images.length - 1 : prev - 1
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900">
      {/* Hero Section */}
      <div className="relative pt-24 pb-16 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-6xl md:text-8xl font-black mb-6 bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
              Social Contribution
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 mx-auto mb-8 rounded-full"></div>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Committed to creating positive change in communities through
              education, healthcare, and sustainable development initiatives
              across Nepal.
            </p>
          </div>

          {/* Impact Statistics */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {impactStats.map((stat, index) => (
              <div
                key={index}
                className="group bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:border-cyan-500/50 transition-all duration-500 hover:scale-105 text-center"
              >
                <div className="text-cyan-400 mb-4 flex justify-center group-hover:scale-110 transition-transform duration-300">
                  <stat.icon className="w-8 h-8" />
                </div>
                <div className="text-4xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-lg font-semibold text-cyan-300 mb-2">
                  {stat.label}
                </div>
                <div className="text-sm text-gray-400">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contributions Grid */}
      <div className="px-6 lg:px-20 py-16">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-16">
            {contributions.map((contribution, index) => (
              <div
                key={index}
                className={`contribution-item transition-all duration-1000 ${
                  visibleItems.includes(index.toString())
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-12"
                }`}
              >
                <div className="bg-white/5 backdrop-blur-xl rounded-3xl overflow-hidden border border-white/10 hover:border-cyan-500/50 transition-all duration-500 group">
                  <div
                    className={`grid lg:grid-cols-5 gap-0 ${
                      index % 2 === 0 ? "" : "lg:grid-flow-col-dense"
                    }`}
                  >
                    {/* Image Gallery Section */}
                    <div
                      className={`lg:col-span-2 relative overflow-hidden ${
                        index % 2 === 0 ? "" : "lg:order-last"
                      }`}
                    >
                      <div className="aspect-square lg:aspect-[4/3] relative">
                        <img
                          src={contribution.images[0]}
                          alt={contribution.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                        {/* Image Count Badge */}
                        <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm rounded-full px-3 py-2 text-sm font-medium text-white flex items-center space-x-2">
                          <Image className="w-4 h-4" />
                          <span>{contribution.images.length}</span>
                        </div>

                        {/* Gallery Overlay */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <button
                            onClick={() => openGallery(index)}
                            className="px-6 py-3 bg-white/20 backdrop-blur-xl rounded-full font-semibold text-white hover:bg-white/30 transition-all duration-300 flex items-center space-x-2"
                          >
                            <Eye className="w-5 h-5" />
                            <span>View Gallery</span>
                          </button>
                        </div>

                        {/* Icon */}
                        <div
                          className={`absolute bottom-4 left-4 w-16 h-16 bg-gradient-to-r ${contribution.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
                        >
                          <contribution.icon className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="lg:col-span-3 p-8 lg:p-12 flex flex-col justify-center">
                      <div className="space-y-6">
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <span className="px-4 py-2 bg-cyan-500/20 text-cyan-300 rounded-full text-sm font-medium">
                              {contribution.type}
                            </span>
                            <span className="text-cyan-400 font-semibold">
                              {contribution.impact}
                            </span>
                          </div>
                          <h3 className="text-3xl md:text-4xl font-bold text-white group-hover:text-cyan-300 transition-colors duration-300 mb-4">
                            {contribution.title}
                          </h3>
                          <p className="text-gray-300 text-lg leading-relaxed mb-4">
                            {contribution.detail}
                          </p>
                          <p className="text-gray-400 leading-relaxed">
                            {contribution.description}
                          </p>
                        </div>

                        {/* Achievements */}
                        <div>
                          <h4 className="text-lg font-semibold text-cyan-300 mb-3">
                            Key Achievements:
                          </h4>
                          <div className="grid grid-cols-2 gap-3">
                            {contribution.achievements.map(
                              (achievement, aIndex) => (
                                <div
                                  key={aIndex}
                                  className="flex items-center space-x-3 text-gray-300"
                                >
                                  <div className="w-3 h-3 bg-cyan-400 rounded-full"></div>
                                  <span className="font-medium">
                                    {achievement}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </div>

                        {/* Action Button */}
                        <div className="pt-4">
                          <button
                            onClick={() => openGallery(index)}
                            className={`inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r ${contribution.color} rounded-full font-semibold text-white hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 hover:scale-105`}
                          >
                            <Image className="w-4 h-4" />
                            <span>View Impact Gallery</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gallery Modal */}
      {selectedProject !== null && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-xl flex items-center justify-center p-4">
          <div className="max-w-6xl w-full max-h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 text-white">
              <div>
                <h3 className="text-2xl font-bold">
                  {contributions[selectedProject].title}
                </h3>
                <p className="text-cyan-400">
                  {contributions[selectedProject].type}
                </p>
              </div>
              <button
                onClick={closeGallery}
                className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors text-2xl"
              >
                ×
              </button>
            </div>

            {/* Image Display */}
            <div className="relative flex-1 flex items-center justify-center">
              <button
                onClick={prevImage}
                className="absolute left-4 z-10 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors text-white text-xl"
              >
                ←
              </button>

              <img
                src={contributions[selectedProject].images[selectedImage]}
                alt={`${contributions[selectedProject].title} ${
                  selectedImage + 1
                }`}
                className="max-w-full max-h-full object-contain rounded-2xl"
              />

              <button
                onClick={nextImage}
                className="absolute right-4 z-10 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors text-white text-xl"
              >
                →
              </button>
            </div>

            {/* Thumbnail Navigation */}
            <div className="flex justify-center space-x-3 mt-6">
              {contributions[selectedProject].images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    index === selectedImage
                      ? "border-cyan-400 scale-110"
                      : "border-transparent hover:border-white/50"
                  }`}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mission Statement */}
      <div className="px-6 lg:px-20 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/10 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10"></div>
            <div className="relative z-10">
              <Heart className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Our Mission
              </h3>
              <blockquote className="text-xl md:text-2xl text-gray-200 italic leading-relaxed mb-8">
                "True success is measured not by what we achieve for ourselves,
                but by what we contribute to lifting others up. Every life we
                touch, every student we support, and every community we help
                strengthens the fabric of our society."
              </blockquote>
              <div className="w-16 h-1 bg-gradient-to-r from-cyan-500 to-blue-500 mx-auto rounded-full mb-8"></div>

              {/* Call to Action */}
              <div className="space-y-6">
                <p className="text-lg text-gray-300 leading-relaxed">
                  Join us in making a difference. Together, we can create
                  lasting positive change in communities across Nepal and
                  beyond.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button className="px-8 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-full font-semibold text-white hover:from-blue-600 hover:to-cyan-600 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/25">
                    <span className="flex items-center space-x-2">
                      <Handshake className="w-5 h-5" />
                      <span>Get Involved</span>
                    </span>
                  </button>
                  <button className="px-8 py-4 border-2 border-white/20 rounded-full font-semibold text-white hover:bg-white/10 transition-all duration-300">
                    <span className="flex items-center space-x-2">
                      <Heart className="w-5 h-5" />
                      <span>Support Our Cause</span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Background Elements */}
      <div className="fixed inset-0 pointer-events-none opacity-5">
        <div
          className="absolute w-96 h-96 bg-gradient-to-r from-cyan-500/30 to-blue-500/30 rounded-full blur-3xl"
          style={{
            transform: `translate(${scrollY * 0.1}px, ${scrollY * 0.05}px)`,
            left: "15%",
            top: "30%",
          }}
        ></div>
        <div
          className="absolute w-96 h-96 bg-gradient-to-r from-blue-500/30 to-indigo-500/30 rounded-full blur-3xl"
          style={{
            transform: `translate(-${scrollY * 0.1}px, -${scrollY * 0.05}px)`,
            right: "15%",
            bottom: "30%",
          }}
        ></div>
      </div>
    </div>
  );
}
